import { normalizeTopicKey, parseTopics } from '../topics/topic.utils';

/**
 * Lightweight, explainable ranking for search People results (P3.D4).
 * Not a search engine — stable heuristic until FTS/Meilisearch at beta.
 *
 * Score weights:
 *   exact username match     +100
 *   topic label match        +50
 *   displayName starts with  +30
 *   headline/bio contains    +10
 * Tie-break: displayName ascending (case-insensitive).
 */
export type SearchPeopleRankInput = {
  userId: string;
  username: string;
  displayName: string;
  topics?: string[] | null;
  headline?: string | null;
  bio?: string | null;
  isCreator?: boolean;
};

export function computeSearchPeopleScore(term: string, entry: SearchPeopleRankInput): number {
  const normalized = term.trim().toLowerCase();
  if (!normalized) return 0;

  let score = 0;
  const username = entry.username.trim().toLowerCase();
  const displayName = entry.displayName.trim().toLowerCase();
  const headline = (entry.headline ?? '').trim().toLowerCase();
  const bio = (entry.bio ?? '').trim().toLowerCase();

  if (username === normalized) score += 100;
  else if (username.includes(normalized)) score += 40;

  const topicKeys = parseTopics(entry.topics).map((topic) => topic.toLowerCase());
  const topicKeyMatches = parseTopics(entry.topics).some((topic) => {
    const key = normalizeTopicKey(topic);
    return key.includes(normalizeTopicKey(normalized)) || topic.toLowerCase().includes(normalized);
  });
  if (topicKeyMatches) score += 50;

  if (displayName.startsWith(normalized)) score += 30;
  else if (displayName.includes(normalized)) score += 15;

  if (headline.includes(normalized) || bio.includes(normalized)) score += 10;

  void topicKeys;
  return score;
}

export function rankSearchPeople<T extends SearchPeopleRankInput>(
  term: string,
  entries: T[],
): T[] {
  return [...entries].sort((left, right) => {
    const scoreDiff = computeSearchPeopleScore(term, right) - computeSearchPeopleScore(term, left);
    if (scoreDiff !== 0) return scoreDiff;
    return left.displayName.localeCompare(right.displayName, undefined, { sensitivity: 'base' });
  });
}

export function matchedTopicsForSearchTerm(
  term: string,
  topics: string[] | null | undefined,
  limit = 2,
): string[] {
  const normalized = term.trim().toLowerCase();
  const normalizedKey = normalizeTopicKey(normalized);
  if (!normalized) return [];
  return parseTopics(topics)
    .filter(
      (topic) =>
        topic.toLowerCase().includes(normalized) ||
        normalizeTopicKey(topic).includes(normalizedKey),
    )
    .slice(0, limit);
}
